import { useState } from "react";
import { connect } from "react-redux";
import {
  APPLY_CHAT_FILTERS,
  TOGGLE_FILTER_PANEL,
  emptyPreFilters,
} from "redux/chat";
import { yearFilters, entityType } from "utils/commons";
import trackEvent from "utils/trackEvent";
import CustomSelect from "components/CustomSelect";
import FiltersIcon from "images/filters2.svg";
import "./style.scss";

// "Grey out Time/OU when an offering-type result is detected" - derived
// from the last assistant message's documents (all OFFERING_TOPIC).
// There's no explicit backend flag for this yet (see README open items).
const deriveIsOfferingOnly = messages => {
  const lastAssistant = [...(messages || [])]
    .reverse()
    .find(m => m.role === "assistant");
  const documents = lastAssistant?.documents || [];
  if (!documents.length) return false;
  return documents.every(d => d.type === entityType.OFFERING_TOPIC);
};

const yearsToRange = (yearFrom, yearTo) => {
  if (!yearFrom && !yearTo) return [];
  const from = Number(yearFrom || yearTo);
  const to = Number(yearTo || yearFrom);
  const min = Math.min(from, to);
  const max = Math.max(from, to);
  const years = [];
  for (let y = min; y <= max; y++) years.push(y);
  return years;
};

const rangeToYears = years => {
  if (!years || !years.length) return { yearFrom: "", yearTo: "" };
  return {
    yearFrom: String(Math.min(...years)),
    yearTo: String(Math.max(...years)),
  };
};

const ChatFilterPanel = ({
  preFilters,
  customers: customerDictionary,
  industries,
  countries,
  technologies,
  organizationalUnits,
  isOfferingOnly,
  isOpen,
  toggleOpen,
  applyFilters,
}) => {
  const initialYears = rangeToYears(preFilters.years);
  const [customers, setCustomers] = useState(preFilters.customers || []);
  const [industryVal, setIndustryVal] = useState(preFilters.industries || []);
  const [yearFrom, setYearFrom] = useState(initialYears.yearFrom);
  const [yearTo, setYearTo] = useState(initialYears.yearTo);
  const [ou, setOu] = useState(preFilters.organizational_units || []);
  const [country, setCountry] = useState(preFilters.countries || []);
  const [technology, setTechnology] = useState(preFilters.tags || []);

  const hasActiveFilters =
    customers.length > 0 ||
    industryVal.length > 0 ||
    !!yearFrom ||
    !!yearTo ||
    ou.length > 0 ||
    country.length > 0 ||
    technology.length > 0;

  const customerOptions = (customerDictionary || []).map(c => ({
    id: c.id,
    value: c.name,
  }));
  const industryOptions = (industries || []).map(i => ({
    id: i.id,
    value: i.name,
  }));
  const countryOptions = (countries || []).map(c => ({
    id: c.id,
    value: c.name,
  }));
  const ouOptions = (organizationalUnits || []).map(o => ({
    id: o.id,
    value: o.name,
  }));
  const technologyOptions = (technologies || []).map(t => ({
    id: t.id,
    value: t.name,
  }));
  const yearOptions = yearFilters.map(y => ({ id: y, value: String(y) }));

  const handleApply = () => {
    const newFilters = {
      ...emptyPreFilters,
      customers,
      industries: industryVal,
      years: isOfferingOnly ? [] : yearsToRange(yearFrom, yearTo),
      organizational_units: isOfferingOnly ? [] : ou,
      countries: country,
      tags: technology,
    };
    trackEvent("CHAT_APPLY_FILTERS", { filters: newFilters });
    applyFilters(newFilters);
  };

  const handleClear = () => {
    setCustomers([]);
    setIndustryVal([]);
    setYearFrom("");
    setYearTo("");
    setOu([]);
    setCountry([]);
    setTechnology([]);
    applyFilters({ ...emptyPreFilters });
  };

  return (
    <div className="chat-filter-panel">
      <button
        type="button"
        className="chat-filter-icon-btn"
        title="Filters"
        aria-label="Filters"
        onClick={toggleOpen}>
        <img src={FiltersIcon} alt="" />
        {hasActiveFilters && <span className="chat-filter-active-dot" />}
      </button>
      {isOpen && (
        <div className="chat-filter-popover">
          <div className="chat-filter-popover-header">
            <span>Filters</span>
            <div className="chat-filter-popover-header-actions">
              {hasActiveFilters && (
                <button
                  type="button"
                  className="chat-filter-clear"
                  onClick={handleClear}>
                  Clear all
                </button>
              )}
              <button
                type="button"
                className="chat-filter-close-btn"
                aria-label="Close filters"
                onClick={toggleOpen}>
                &times;
              </button>
            </div>
          </div>
          <div className="chat-filter-popover-row">
            <div className="chat-filter-field">
              <CustomSelect
                label="Client"
                value={customers}
                items={customerOptions}
                onChange={setCustomers}
                isMultiple
              />
            </div>
            <div className="chat-filter-field">
              <CustomSelect
                label="Industry"
                value={industryVal}
                items={industryOptions}
                onChange={setIndustryVal}
                isMultiple
              />
            </div>
            <div
              className={`chat-filter-field ${
                isOfferingOnly ? "chat-filter-field-disabled" : ""
              }`}
              title={
                isOfferingOnly
                  ? "Time filters are ignored for offering results"
                  : undefined
              }>
              <CustomSelect
                label="Year from"
                value={yearFrom}
                items={yearOptions}
                onChange={isOfferingOnly ? () => {} : setYearFrom}
              />
            </div>
            <div
              className={`chat-filter-field ${
                isOfferingOnly ? "chat-filter-field-disabled" : ""
              }`}
              title={
                isOfferingOnly
                  ? "Time filters are ignored for offering results"
                  : undefined
              }>
              <CustomSelect
                label="Year to"
                value={yearTo}
                items={yearOptions}
                onChange={isOfferingOnly ? () => {} : setYearTo}
              />
            </div>
            <div
              className={`chat-filter-field ${
                isOfferingOnly ? "chat-filter-field-disabled" : ""
              }`}
              title={
                isOfferingOnly
                  ? "OU filters are ignored for offering results"
                  : undefined
              }>
              <CustomSelect
                label="Organizational Unit"
                value={ou}
                items={ouOptions}
                onChange={isOfferingOnly ? () => {} : setOu}
                isMultiple
              />
            </div>
            <div className="chat-filter-field">
              <CustomSelect
                label="Country"
                value={country}
                items={countryOptions}
                onChange={setCountry}
                isMultiple
              />
            </div>
            <div className="chat-filter-field">
              <CustomSelect
                label="Technology"
                value={technology}
                items={technologyOptions}
                onChange={setTechnology}
                isMultiple
              />
            </div>
            <button
              type="button"
              className="chat-filter-apply-btn"
              onClick={handleApply}>
              Apply filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default connect(
  ({
    chat: { preFilters, isFilterPanelOpen, messages },
    dictionaries,
    organizationalUnits,
    industries,
  }) => ({
    preFilters,
    isOpen: isFilterPanelOpen,
    customers: dictionaries?.CUSTOMER,
    // Bug fix: dictionaries.INDUSTRY is never fetched on this page's boot
    // path (only IndustriesBubbles fetches that GET_DICTIONARY type,
    // elsewhere in the app) - it stayed permanently empty here, so the
    // Industry filter never had any options. redux/industries.js's own
    // top-level `industries` slice (the same one the traditional search's
    // Filters component uses for its Industry field) is already fetched
    // unconditionally at AppRouter's boot (getIndustries()), so it's
    // reliably populated by the time this panel can be opened.
    industries,
    countries: dictionaries?.COUNTRY,
    technologies: dictionaries?.TECHNOLOGY,
    organizationalUnits,
    isOfferingOnly: deriveIsOfferingOnly(messages),
  }),
  dispatch => ({
    toggleOpen: () => dispatch(TOGGLE_FILTER_PANEL()),
    applyFilters: filters => dispatch(APPLY_CHAT_FILTERS(filters)),
  })
)(ChatFilterPanel);
